import React from 'react'
import { useTranslation } from 'react-i18next'
import { Film, Headphones, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLibraryMeta } from './libraryMeta'
import type { MediaLibraryItem } from '@/lib/sdk-official/resources/media'

/**
 * One file of the Media library: kind, name, the line that tells it apart, where it came from and
 * "usado em N lugares". `as="button"` in the picker (the whole row picks); a `div` with `action`
 * on the team's screen, so no button sits inside another.
 */
export const MediaLibraryRow = ({ item, as = 'div', onClick, action, selected, className }: {
  item: MediaLibraryItem
  as?: 'div' | 'button'
  onClick?: () => void
  action?: React.ReactNode
  selected?: boolean
  className?: string
}) => {
  const { t } = useTranslation('media')
  const meta = useLibraryMeta()
  const Icon = item.kind === 'audio' ? Headphones : Film
  const badges = (
    <>
      <span className="whitespace-nowrap rounded-full bg-surface-muted px-2 py-0.5 text-xs text-foreground-secondary" data-testid="library-used-in">
        {t('library.usedIn', { count: item.usedIn })}
      </span>
      {item.paidOrigin && (
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-amber-700"><Lock className="h-3 w-3" aria-hidden />{t('library.paidOrigin')}</span>
      )}
    </>
  )
  const body = (
    <>
      <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary" aria-hidden>
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block break-words text-sm font-medium text-foreground">{item.name}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{meta(item)}</span>
        {item.source?.title && (
          <span className="mt-0.5 block break-words text-xs text-foreground-secondary">
            {t(`library.source.${item.source.type}`, { title: item.source.title, defaultValue: item.source.title })}
          </span>
        )}
        <span className="mt-1.5 flex flex-wrap items-center gap-2 sm:hidden">{badges}</span>
      </span>
      <span className="hidden flex-shrink-0 flex-col items-end gap-1 text-right sm:flex">{badges}</span>
      {action}
    </>
  )
  const rowClass = cn(
    'flex w-full min-w-0 items-start gap-3 rounded-lg border p-3 text-left',
    selected ? 'border-primary bg-primary-50/40' : 'border-border bg-surface',
    className,
  )
  if (as === 'button') {
    return (
      <button type="button" onClick={onClick} aria-pressed={selected} className={cn(rowClass, 'transition-colors hover:border-primary hover:bg-primary-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200')}
        data-testid="library-item" data-media-id={item.id}>
        {body}
      </button>
    )
  }
  return <div className={rowClass} data-testid="library-item" data-media-id={item.id}>{body}</div>
}
